import { counterpartApiConnector } from 'apiConnector/counterpart/counterpart';
import classNames from 'classnames';
import { CompactSelector } from 'components/shared/compact-selector';
import { useSalesCounterpartButton } from 'components/shared/compact-selector/use-toggle-button';
import { SalesCounterPartResult } from 'components/shared/SalesCounterPart/SalesCounterPartResult';
import { SelectSearch } from 'components/shared/SelectSearch/SelectSearch';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC } from 'react';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { map } from 'rxjs/internal/operators/map';
import { httpClient } from 'utils/http/httpClient';

import { LoadingFieldSkeleton } from '../../FieldSkeleton';
import type { ClientSelectorProps } from './model';

const apiConnector = counterpartApiConnector(httpClient);

export async function getCounterpartFromEliotCode(eliotCode: string): Promise<ISalesCounterpart | null> {
  const response$ = apiConnector.fetchSalesCounterpartsFromEliotCodes([eliotCode]).pipe(
    map(clients =>
      clients.map(
        client => ({ id: client.id, label: client.label, eliotCode: client.eliotCode }) satisfies ISalesCounterpart,
      ),
    ),
    map(clients => (clients.length > 0 ? clients[0] : null)),
  );
  return await firstValueFrom(response$);
}

export const ClientSelector: FC<ClientSelectorProps> = ({
  loadingStatus,
  productClients,
  selectedClient,
  searchActiveOnly,
  isDisabled = false,
  onClientChanged,
  onClientClear,
  className,
  getErrors,
  autoOpen,
  defaultSearchedValue,
  onOpenChange,
  isReadOnly = false,
}) => {
  const { label, className: buttonClassName } = useSalesCounterpartButton(selectedClient, true);
  if (loadingStatus === 'loading') {
    return <LoadingFieldSkeleton className="flex-grow-1" />;
  }
  return (
    <CompactSelector
      autoOpen={autoOpen}
      onOpenChange={onOpenChange}
      isReadonly={isReadOnly}
      renderButton={(ref, onClick) => (
        <CompactSelector.ToggleButton
          disabled={isDisabled}
          isReadonly={isReadOnly}
          id="client"
          e2ePrefix="client"
          label={label}
          className={classNames(buttonClassName, 'justify-content-start')}
          onClick={onClick}
          ref={ref}
        />
      )}
      hasClearButton={false}
      selectedValue={selectedClient ?? null}
      isDisabled={isDisabled}
      hasBorder
      className={className}
      onSelectedItemChanged={client => {
        if (client) {
          onClientChanged(client);
        } else {
          onClientClear();
        }
      }}
      renderContent={onSelectedChanged => (
        <SelectSearch
          autofocus
          disabled={isDisabled}
          getSearchResult={query => apiConnector.fetchSalesCounterparts(query, searchActiveOnly)}
          syncItems={productClients}
          onValueSelected={onSelectedChanged}
          resultRenderer={SalesCounterPartResult}
          deduplicateComparator={(client1, client2) => client1.id === client2.id}
          getErrorForItem={getErrors}
          defaultValue={defaultSearchedValue}
        />
      )}
    ></CompactSelector>
  );
};

ClientSelector.displayName = 'ClientSelector';
